import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

interface Fabricante {
  CODIGO: string;
  DESCRICAO: string;
  AD_LUCRO: string;
  AD_CUSTO: string;
  AD_PRAZO_ATUAL_DIAS: string;
  AD_ORIGEM: string;
  TEM_PRODUTOS: boolean;
}

interface FormFabricante {
  descricao: string;
  margemLucro: string;
  margemCusto: string;
  prazoAtualizacao: string;
  origem: string;
}

type AcaoPendente = { tipo: 'excluir'; fabricante: Fabricante };
type CampoOrdenacao = 'codigo' | 'descricao' | 'custo' | 'lucro' | 'origem' | 'prazo';

const CAMPOS = ['CODIGO', 'DESCRICAO', 'AD_LUCRO', 'AD_CUSTO', 'AD_PRAZO_ATUAL_DIAS', 'AD_ORIGEM', 'TEM_PRODUTOS'];

@Component({
  selector: 'app-fabricantes',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent],
  templateUrl: './fabricantes.component.html',
  styleUrl: './fabricantes.component.css'
})
export class FabricantesComponent implements OnInit {
  itensPorPagina = 11;
  readonly opcoesPorPagina = [11, 25, 50, 100];
  readonly formatadorPercentual = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  fabricantes: Fabricante[] = [];
  carregando = false;
  salvando = false;
  excluindo = false;
  erro = '';
  erroModal = '';
  sucesso = '';
  busca = '';
  origemFiltro = '';
  verificacaoProdutosConcluida = false;
  pagina = 1;
  campoOrdenacao: CampoOrdenacao | null = null;
  ordemAscendente = true;
  ordenando = false;
  selecionado: Fabricante | null = null;
  formularioAberto = false;
  criando = false;
  acaoPendente: AcaoPendente | null = null;
  avisoExclusao: { fabricante: Fabricante; motivo: 'produtos' | 'nao-verificado' } | null = null;
  form: FormFabricante = this.formularioVazio();
  dropdownAberto: string | null = null;
  dropdownPos: { top: number; right: number } | null = null;

  constructor(private readonly gateway: SankhyaGatewayService) {}

  ngOnInit(): void { this.carregar(); }

  get fabricantesFiltrados(): Fabricante[] {
    const termo = this.normalizar(this.busca);
    const filtrados = this.fabricantes.filter(fabricante => {
      const texto = this.normalizar(`${fabricante.CODIGO} ${fabricante.DESCRICAO}`);
      const origem = !this.origemFiltro || this.normalizar(fabricante.AD_ORIGEM) === this.normalizar(this.origemFiltro);
      return origem && (!termo || texto.includes(termo));
    });
    if (!this.campoOrdenacao) return filtrados;

    return filtrados.sort((a, b) => {
      const valorA = this.valorOrdenacao(a, this.campoOrdenacao!);
      const valorB = this.valorOrdenacao(b, this.campoOrdenacao!);
      const comparacao = typeof valorA === 'number' && typeof valorB === 'number'
        ? valorA - valorB
        : String(valorA).localeCompare(String(valorB), 'pt-BR', { sensitivity: 'base' });
      return this.ordemAscendente ? comparacao : -comparacao;
    });
  }

  get fabricantesPagina(): Fabricante[] {
    const inicio = (this.pagina - 1) * this.itensPorPagina;
    return this.fabricantesFiltrados.slice(inicio, inicio + this.itensPorPagina);
  }

  get totalPaginas(): number { return Math.max(1, Math.ceil(this.fabricantesFiltrados.length / this.itensPorPagina)); }
  get paginas(): number[] {
    const delta = 2;
    const paginas: number[] = [];
    for (let atual = Math.max(1, this.pagina - delta); atual <= Math.min(this.totalPaginas, this.pagina + delta); atual++) paginas.push(atual);
    return paginas;
  }
  get nacionais(): number { return this.fabricantes.filter(f => this.normalizar(f.AD_ORIGEM) === 'NACIONAL').length; }
  get internacionais(): number { return this.fabricantes.filter(f => this.normalizar(f.AD_ORIGEM) === 'INTERNACIONAL').length; }

  carregar(): void {
    this.carregando = true;
    this.erro = '';
    this.sucesso = '';
    this.gateway.buscarFabricantesNativo().subscribe({
      next: (resposta: any) => {
        const body = resposta?.responseBody ?? resposta?.serviceResponse?.responseBody ?? resposta;
        this.fabricantes = (Array.isArray(body?.result) ? body.result : []).map((registro: any) => this.mapearFabricante(registro));
        this.verificacaoProdutosConcluida = body?.verificacaoProdutosConcluida === true;
        this.pagina = 1;
        this.carregando = false;
        if (resposta?.status && String(resposta.status) !== '1') {
          this.erro = resposta?.statusMessage || 'Não foi possível consultar os fabricantes.';
        }
      },
      error: (falha: any) => {
        this.carregando = false;
        this.erro = falha?.error?.detail ?? falha?.error?.message ?? 'Falha ao consultar os fabricantes. Tente novamente.';
      }
    });
  }

  abrirNovo(): void {
    this.fecharDropdown();
    this.selecionado = null;
    this.criando = true;
    this.form = this.formularioVazio();
    this.erroModal = '';
    this.formularioAberto = true;
  }

  abrirEdicao(fabricante: Fabricante): void {
    this.fecharDropdown();
    this.selecionado = fabricante;
    this.criando = false;
    this.form = {
      descricao: fabricante.DESCRICAO,
      margemLucro: fabricante.AD_LUCRO,
      margemCusto: fabricante.AD_CUSTO,
      prazoAtualizacao: fabricante.AD_PRAZO_ATUAL_DIAS,
      origem: fabricante.AD_ORIGEM
    };
    this.erroModal = '';
    this.formularioAberto = true;
  }

  solicitarExclusao(fabricante: Fabricante): void {
    this.fecharDropdown();
    this.acaoPendente = { tipo: 'excluir', fabricante };
  }

  toggleDropdown(event: Event, codigo: string): void {
    event.stopPropagation();
    if (this.dropdownAberto === codigo) { this.fecharDropdown(); return; }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const alturaMenu = 92;
    this.dropdownAberto = codigo;
    this.dropdownPos = {
      top: window.innerHeight - rect.bottom < alturaMenu + 12 ? Math.max(8, rect.top - alturaMenu - 6) : rect.bottom + 6,
      right: window.innerWidth - rect.right
    };
  }

  fecharDropdown(): void { this.dropdownAberto = null; this.dropdownPos = null; }

  @HostListener('document:click')
  fecharDropdownAoClicarFora(): void { this.fecharDropdown(); }

  avisarExclusaoBloqueada(fabricante: Fabricante): void {
    this.avisoExclusao = {
      fabricante,
      motivo: this.verificacaoProdutosConcluida ? 'produtos' : 'nao-verificado'
    };
  }

  fecharAvisoExclusao(): void { this.avisoExclusao = null; }

  cancelarConfirmacao(): void {
    if (this.excluindo) return;
    this.acaoPendente = null;
  }

  confirmarAcao(): void {
    if (!this.acaoPendente) return;
    const acao = this.acaoPendente;
    this.excluir(acao.fabricante);
  }

  fecharFormulario(): void {
    if (this.salvando) return;
    this.formularioAberto = false;
    this.selecionado = null;
    this.criando = false;
    this.erroModal = '';
  }

  salvar(): void {
    if (this.salvando) return;
    this.erroModal = '';
    if (!this.form.descricao.trim()) { this.erroModal = 'Informe a descrição do fabricante.'; return; }
    if (!this.form.origem) { this.erroModal = 'Selecione a origem do produto.'; return; }
    if (!this.form.prazoAtualizacao.trim()) { this.erroModal = 'Informe o prazo para atualização.'; return; }

    this.salvando = true;
    const payload = {
      descricao: this.form.descricao.trim(),
      margemLucro: this.form.margemLucro,
      margemCusto: this.form.margemCusto,
      prazoAtualizacao: this.form.prazoAtualizacao.trim(),
      origem: this.form.origem
    };
    let dadosAlterados: Partial<FormFabricante> = payload;
    if (!this.criando && this.selecionado) {
      const original = this.selecionado;
      dadosAlterados = {};
      if (payload.descricao !== original.DESCRICAO) dadosAlterados.descricao = payload.descricao;
      if (payload.margemLucro !== original.AD_LUCRO) dadosAlterados.margemLucro = payload.margemLucro;
      if (payload.margemCusto !== original.AD_CUSTO) dadosAlterados.margemCusto = payload.margemCusto;
      if (payload.prazoAtualizacao !== original.AD_PRAZO_ATUAL_DIAS) dadosAlterados.prazoAtualizacao = payload.prazoAtualizacao;
      if (payload.origem !== original.AD_ORIGEM) dadosAlterados.origem = payload.origem;
      if (!Object.keys(dadosAlterados).length) {
        this.salvando = false;
        this.erroModal = 'Nenhuma alteração foi feita.';
        return;
      }
    }
    const requisicao = this.criando
      ? this.gateway.criarFabricanteNativo(payload)
      : this.gateway.salvarFabricanteNativo({ codigo: Number(this.selecionado!.CODIGO), ...dadosAlterados });

    requisicao.subscribe({
      next: (resposta: any) => {
        if (resposta?.status && String(resposta.status) !== '1') {
          this.erroModal = resposta?.statusMessage || 'O Sankhya não confirmou o salvamento.';
          this.salvando = false;
          return;
        }
        const novo = this.fabricanteSalvo(resposta, payload, dadosAlterados);
        if (this.criando) {
          if (novo.CODIGO) this.fabricantes = [novo, ...this.fabricantes.filter(f => f.CODIGO !== novo.CODIGO)];
          this.sucesso = 'Fabricante cadastrado.';
        } else {
          const atual = this.fabricantes.find(f => f.CODIGO === this.selecionado?.CODIGO);
          if (atual) Object.assign(atual, novo, { CODIGO: atual.CODIGO });
          this.sucesso = 'Fabricante atualizado.';
        }
        this.salvando = false;
        this.fecharFormulario();
        setTimeout(() => this.sucesso = '', 3500);
      },
      error: (falha: any) => {
        this.salvando = false;
        this.erroModal = falha?.error?.detail ?? falha?.error?.message ?? 'Não foi possível salvar o fabricante.';
      }
    });
  }

  private excluir(fabricante: Fabricante): void {
    this.excluindo = true;
    this.erro = '';
    this.gateway.excluirFabricanteNativo(Number(fabricante.CODIGO)).subscribe({
      next: (resposta: any) => {
        if (resposta?.status && String(resposta.status) !== '1') {
          this.erro = resposta?.statusMessage || 'O Sankhya não confirmou a exclusão.';
          this.excluindo = false;
          this.acaoPendente = null;
          return;
        }
        this.fabricantes = this.fabricantes.filter(f => f.CODIGO !== fabricante.CODIGO);
        this.acaoPendente = null;
        this.excluindo = false;
        if (this.pagina > this.totalPaginas) this.pagina = this.totalPaginas;
        this.sucesso = 'Fabricante excluído.';
        setTimeout(() => this.sucesso = '', 3500);
      },
      error: (falha: any) => {
        this.excluindo = false;
        this.acaoPendente = null;
        this.erro = falha?.error?.detail ?? falha?.error?.message ?? 'Não foi possível excluir o fabricante.';
      }
    });
  }

  private fabricanteSalvo(resposta: any, payload: FormFabricante, dadosAlterados: Partial<FormFabricante>): Fabricante {
    const body = resposta?.responseBody ?? resposta?.serviceResponse?.responseBody ?? resposta;
    const linha = Array.isArray(body?.result) ? body.result[0] : undefined;
    if (linha) {
      const fabricante = this.mapearFabricante(linha);
      if (fabricante.CODIGO) {
        if (!this.criando) fabricante.TEM_PRODUTOS = this.selecionado?.TEM_PRODUTOS ?? false;
        return fabricante;
      }
    }
    const base = this.selecionado;
    return {
      CODIGO: base?.CODIGO ?? '',
      DESCRICAO: dadosAlterados.descricao ?? (this.criando ? payload.descricao : base?.DESCRICAO ?? ''),
      AD_LUCRO: dadosAlterados.margemLucro ?? (this.criando ? payload.margemLucro : base?.AD_LUCRO ?? ''),
      AD_CUSTO: dadosAlterados.margemCusto ?? (this.criando ? payload.margemCusto : base?.AD_CUSTO ?? ''),
      AD_PRAZO_ATUAL_DIAS: dadosAlterados.prazoAtualizacao ?? (this.criando ? payload.prazoAtualizacao : base?.AD_PRAZO_ATUAL_DIAS ?? ''),
      AD_ORIGEM: dadosAlterados.origem ?? (this.criando ? payload.origem : base?.AD_ORIGEM ?? ''),
      TEM_PRODUTOS: base?.TEM_PRODUTOS ?? false
    };
  }

  alterarBusca(): void { this.pagina = 1; }
  onItensPorPaginaChange(): void { this.pagina = 1; }
  ordenarPor(campo: CampoOrdenacao): void {
    if (this.ordenando) return;
    const ascendente = this.campoOrdenacao === campo ? !this.ordemAscendente : true;
    this.ordenando = true;
    requestAnimationFrame(() => setTimeout(() => {
      this.campoOrdenacao = campo;
      this.ordemAscendente = ascendente;
      this.pagina = 1;
      this.ordenando = false;
    }, 120));
  }
  classeOrdenacao(campo: CampoOrdenacao): string {
    if (this.campoOrdenacao !== campo) return '';
    return this.ordemAscendente ? 'ds-table-sort--asc' : 'ds-table-sort--desc';
  }
  sentidoOrdenacao(campo: CampoOrdenacao): string {
    return this.campoOrdenacao !== campo ? 'none' : this.ordemAscendente ? 'ascending' : 'descending';
  }
  irParaPagina(pagina: number): void { this.pagina = Math.min(this.totalPaginas, Math.max(1, pagina)); }
  rastrearFabricante(_indice: number, fabricante: Fabricante): string { return fabricante.CODIGO; }

  exibirPercentual(valor: string): string {
    if (!valor) return '-';
    const numero = Number(String(valor).replace(',', '.'));
    return Number.isFinite(numero) ? this.formatadorPercentual.format(numero) : valor;
  }

  origemLabel(valor: string): string { return valor || 'Não informada'; }
  origemClasse(valor: string): string { return this.normalizar(valor) === 'NACIONAL' ? 'ds-badge--success' : 'ds-badge--info'; }

  private formularioVazio(): FormFabricante {
    return { descricao: '', margemLucro: '', margemCusto: '', prazoAtualizacao: '', origem: 'NACIONAL' };
  }

  private valorOrdenacao(fabricante: Fabricante, campo: CampoOrdenacao): string | number {
    switch (campo) {
      case 'codigo': return this.numeroOrdenacao(fabricante.CODIGO);
      case 'descricao': return this.normalizar(fabricante.DESCRICAO);
      case 'origem': return this.normalizar(fabricante.AD_ORIGEM);
      case 'custo': return this.numeroOrdenacao(fabricante.AD_CUSTO);
      case 'lucro': return this.numeroOrdenacao(fabricante.AD_LUCRO);
      case 'prazo': return this.numeroOrdenacao(fabricante.AD_PRAZO_ATUAL_DIAS);
    }
  }

  private numeroOrdenacao(valor: string): number {
    const numero = Number(String(valor ?? '').replace(',', '.'));
    return Number.isFinite(numero) ? numero : 0;
  }

  private mapearFabricante(registro: any): Fabricante {
    if (Array.isArray(registro)) {
      const values = CAMPOS.map((_campo, index) => this.unwrap(registro[index]));
      return this.fabricanteDeObjeto(Object.fromEntries(CAMPOS.map((campo, index) => [campo, values[index]])));
    }
    if (Array.isArray(registro?.field)) {
      const values = Object.fromEntries(registro.field.map((campo: any) => [campo.fieldName, this.unwrap(campo.$ ?? campo.value)]));
      return this.fabricanteDeObjeto(values);
    }
    return this.fabricanteDeObjeto(registro ?? {});
  }

  private fabricanteDeObjeto(value: Record<string, unknown>): Fabricante {
    return {
      CODIGO: String(value['CODIGO'] ?? ''),
      DESCRICAO: String(value['DESCRICAO'] ?? ''),
      AD_LUCRO: String(value['AD_LUCRO'] ?? ''),
      AD_CUSTO: String(value['AD_CUSTO'] ?? ''),
      AD_PRAZO_ATUAL_DIAS: String(value['AD_PRAZO_ATUAL_DIAS'] ?? ''),
      AD_ORIGEM: String(value['AD_ORIGEM'] ?? ''),
      TEM_PRODUTOS: this.booleano(value['TEM_PRODUTOS'])
    };
  }

  private unwrap(value: any): unknown { return value && typeof value === 'object' && '$' in value ? value.$ : value; }
  private booleano(value: unknown): boolean {
    return value === true || ['S', 'SIM', 'TRUE', '1'].includes(String(value ?? '').trim().toUpperCase());
  }
  private normalizar(value: string): string {
    return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();
  }
}
